import { Form, Input, InputNumber, DatePicker, Select, Spin } from 'antd';
import { FormInstance } from 'antd/es/form';
import FormSection from '../FormSection';
import CurvDown from '../svg/CurvDown';
import Calendar from '../svg/Calendar';

const { Option } = Select;

interface GeneralInformationProps {
  form: FormInstance;
  discriminationBasis?: string;
  caseNo?: number | null;
  loadingCaseNo?: boolean;
}

const GeneralInformation: React.FC<GeneralInformationProps> = ({ form, discriminationBasis, caseNo, loadingCaseNo = false }) => {
  // Get the basis-specific field label
  const getYourBasisLabel = () => {
    switch (discriminationBasis) {
      case 'Age': return 'Your Age is:';
      case 'Color': return 'Your Color is:';
      case 'Mental/Physical Handicap': return 'Your Mental/Physical Handicap is:';
      case 'National Origin': return 'Your National origin is:';
      case 'Race': return 'Your Race is:';
      case 'Religion': return 'Your Religion is:';
      case 'Reprisal': return 'Your Reprisal is:';
      case 'Sex': return 'Your Sex is:';
      default: return 'Your Basis is:';
    }
  };

  const getAccusedBasisLabel = () => {
    switch (discriminationBasis) {
      case 'Age': return "Accused's Age is:";
      case 'Color': return "Accused's Color is:";
      case 'Mental/Physical Handicap': return "Accused's Mental/Physical Handicap is:";
      case 'National Origin': return "Accused's National origin is:";
      case 'Race': return "Accused's Race is:";
      case 'Religion': return "Accused's Religion is:";
      case 'Reprisal': return "Accused's Reprisal is:";
      case 'Sex': return "Accused's Sex is:";
      default: return "Accused Basis is:";
    }
  };

  const getBasisFieldName = (prefix: 'your' | 'accused') => {
    switch (discriminationBasis) {
      case 'Age': return prefix === 'your' ? 'your_age' : 'accused_age';
      case 'Color': return prefix === 'your' ? 'your_color' : 'accused_color';
      case 'Mental/Physical Handicap': return prefix === 'your' ? 'your_handicap' : 'accused_handicap';
      case 'National Origin': return prefix === 'your' ? 'your_national_origin' : 'accused_national_origin';
      case 'Race': return prefix === 'your' ? 'your_race' : 'accused_race';
      case 'Religion': return prefix === 'your' ? 'your_religion' : 'accused_religion';
      case 'Reprisal': return prefix === 'your' ? 'your_reprisal' : 'accused_reprisal';
      case 'Sex': return prefix === 'your' ? 'your_sex' : 'accused_sex';
      default: return null;
    }
  };

  const caseTypeOptions = ['Accommodation', 'Different Treatment', 'Harassment', 'Nonselection', 'Sexual Harassment', 'Termination'];
  const discriminationBasisOptions = ['Age', 'Color', 'Mental/Physical Handicap', 'National Origin', 'Race', 'Religion', 'Reprisal', 'Sex'];

  return (
    <div className="flex flex-col w-full h-full general-info-container">
      <style dangerouslySetInnerHTML={{
        __html: `
        .general-info-container .ant-form-item-label {
          min-height: 30px !important;
        }
      `}} />
      <FormSection title="General Information" columns={3}>
        <Form.Item label="Case Number" name="case_no" initialValue={caseNo}>
          {loadingCaseNo ? (
            <div className="flex items-center h-[45px] px-4 bg-[#F3F4F6] rounded-[8px]"><Spin size="small" /></div>
          ) : (
            <InputNumber placeholder="Auto-generated" disabled value={caseNo || undefined} readOnly style={{ cursor: 'not-allowed' }} />
          )}
        </Form.Item>
        <Form.Item label="Date of Interview" name="date_of_interview" >
          <DatePicker format="DD-MM-YYYY" placeholder="Select Date" suffixIcon={<Calendar />}
            inputReadOnly />
        </Form.Item>
        <Form.Item label="Case Type" name="case_type" rules={[{ required: true, message: 'Please select the case type!' }]}>
          <Select placeholder="Select case type" suffixIcon={<CurvDown className='pt-[10px] md:pt-[14px]' />}>
            {caseTypeOptions.map(option => <Option key={option} value={option}>{option}</Option>)}
          </Select>
        </Form.Item>

        <Form.Item label="Discrimination Basis" name="discrimination_basis" rules={[{ required: true, message: 'Please select the discrimination basis!' }]}>
          <Select placeholder="Select discrimination basis" suffixIcon={<CurvDown className="pt-[10px] md:pt-[14px]" />}>
            {discriminationBasisOptions.map(option => <Option key={option} value={option}>{option}</Option>)}
          </Select>
        </Form.Item>
        <Form.Item label="Date Discrimination Occurred" name="date_discrimination_occurred">
          <DatePicker format="DD-MM-YYYY" placeholder="Select Date" suffixIcon={<Calendar />}
            inputReadOnly />
        </Form.Item>

      </FormSection>

      <FormSection title="Personal Information" isSubSection columns={3} className="md:pt-[14px]">
        <Form.Item label="Last Name" name="last_name">
          <Input placeholder="Last Name" />
        </Form.Item>
        <Form.Item label="Middle Name" name="middle">
          <Input placeholder="Middle Name" />
        </Form.Item>
        <Form.Item label="First Name" name="first_name">
          <Input placeholder="First Name" />
        </Form.Item>

        {discriminationBasis && getBasisFieldName('your') && (
          <Form.Item label={getYourBasisLabel()} name={getBasisFieldName('your')!}>
            {discriminationBasis === 'Age' ? (
              <InputNumber
                placeholder="Enter age"
                className="w-full"
                controls={false}
                onKeyDown={(e) => {
                  if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Tab' && e.key !== 'Delete' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
                    e.preventDefault();
                  }
                }}
              />
            ) : (
              <Input placeholder="Enter here" />
            )}
          </Form.Item>
        )}
      </FormSection>

      <FormSection title="Accused's Information" isSubSection columns={3} className='md:pt-[26px]'>
        <Form.Item label="Company Name" name="company_name">
          <Input placeholder="Company Name" />
        </Form.Item>
        <Form.Item label="Last Name" name="accused_last_name">
          <Input placeholder="Last Name" />
        </Form.Item>
        <Form.Item label="First Name" name="accused_first_name">
          <Input placeholder="First Name" />
        </Form.Item>

        {discriminationBasis && getBasisFieldName('accused') && (
          <Form.Item label={getAccusedBasisLabel()} name={getBasisFieldName('accused')!}>
            {discriminationBasis === 'Age' ? (
              <InputNumber
                placeholder="Enter age"
                className="w-full"
                controls={false}
                onKeyDown={(e) => {
                  if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Tab' && e.key !== 'Delete' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
                    e.preventDefault();
                  }
                }}
              />
            ) : (
              <Input placeholder="Enter here" />
            )}
          </Form.Item>
        )}
      </FormSection>
    </div>
  );
};

export default GeneralInformation;


